import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { VariableSizeList as List } from 'react-window';
import { getCustomer360DetailApiClient, Customer360Detail } from '../../services/customer360DetailApi';
import { getSupabaseServiceKey } from '../../services/auth';
import './PlannerPage.css';

interface Column {
  id: string;
  title: string;
}

interface LeadCard {
  id: string;
  customer: Customer360Detail;
}

const DEFAULT_COLUMNS: Column[] = [
  { id: 'new', title: 'Novos Leads' },
  { id: 'contacted', title: 'Contatados' },
  { id: 'qualified', title: 'Qualificados' },
  { id: 'proposal', title: 'Proposta Enviada' },
  { id: 'negotiation', title: 'Em Negociação' },
  { id: 'won', title: 'Ganhos' },
  { id: 'lost', title: 'Perdidos' },
];

export default function PlannerPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Customer360Detail[]>([]);
  const [columns, setColumns] = useState<Column[]>(() => {
    const saved = localStorage.getItem('planner_columns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [newColumnTitle, setNewColumnTitle] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  useEffect(() => {
    const serviceKey = getSupabaseServiceKey();
    if (!serviceKey) {
      setLoading(false);
      return;
    }
    loadLeads();
  }, []);

  useEffect(() => {
    localStorage.setItem('planner_columns', JSON.stringify(columns));
  }, [columns]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const apiClient = getCustomer360DetailApiClient();
      // Buscar todos os leads (sem paginação para o Kanban)
      const response = await apiClient.searchCustomers('', 1, 10000);
      setLeads(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Agrupar leads por lifecycle_stage
  const leadsByColumn = useMemo(() => {
    const grouped: Record<string, Customer360Detail[]> = {};
    columns.forEach(col => {
      grouped[col.id] = [];
    });

    leads.forEach(lead => {
      const stage = lead.lifecycle_stage || 'new';
      // Se a coluna não existir mais, colocar em 'new'
      if (grouped[stage]) {
        grouped[stage].push(lead);
      } else {
        grouped['new'].push(lead);
      }
    });

    return grouped;
  }, [leads, columns]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setIsDragging(false);

    if (!over) return;

    const leadId = active.id as string;
    const newColumnId = over.id as string;

    // Encontrar o lead
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    // Se arrastou para a mesma coluna, não fazer nada
    if (lead.lifecycle_stage === newColumnId) return;

    // Atualizar localmente primeiro (otimistic update)
    setLeads(prevLeads =>
      prevLeads.map(l =>
        l.id === leadId ? { ...l, lifecycle_stage: newColumnId } : l
      )
    );

    // Atualizar no backend
    try {
      const apiClient = getCustomer360DetailApiClient();
      const serviceKey = getSupabaseServiceKey();
      if (!serviceKey) return;

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL || 'https://supabase.terenceconsultoria.com.br'}/functions/v1/customer-360/${leadId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${serviceKey}`,
          },
          body: JSON.stringify({ lifecycle_stage: newColumnId }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao atualizar lead');
      }
    } catch (error) {
      console.error('Erro ao atualizar lead:', error);
      // Reverter mudança local em caso de erro
      setLeads(prevLeads =>
        prevLeads.map(l =>
          l.id === leadId ? { ...l, lifecycle_stage: lead.lifecycle_stage } : l
        )
      );
    }
  }, [leads]);

  const handleAddColumn = useCallback(() => {
    const newId = `column_${Date.now()}`;
    const newColumn: Column = { id: newId, title: 'Nova Coluna' };
    setColumns([...columns, newColumn]);
    setEditingColumn(newId);
    setNewColumnTitle('Nova Coluna');
  }, []);

  const handleEditColumn = useCallback((columnId: string) => {
    const column = columns.find(c => c.id === columnId);
    if (column) {
      setEditingColumn(columnId);
      setNewColumnTitle(column.title);
    }
  }, [columns]);

  const handleSaveColumn = useCallback(() => {
    if (!editingColumn || !newColumnTitle.trim()) return;

    setColumns(prevColumns =>
      prevColumns.map(col =>
        col.id === editingColumn ? { ...col, title: newColumnTitle.trim() } : col
      )
    );
    setEditingColumn(null);
    setNewColumnTitle('');
  }, [editingColumn, newColumnTitle]);

  const handleDeleteColumn = useCallback((columnId: string) => {
    if (columns.length <= 1) {
      alert('Você precisa ter pelo menos uma coluna!');
      return;
    }

    if (window.confirm('Tem certeza que deseja deletar esta coluna? Os leads serão movidos para a primeira coluna.')) {
      // Mover leads para a primeira coluna
      const firstColumnId = columns[0].id === columnId ? columns[1].id : columns[0].id;
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.lifecycle_stage === columnId
            ? { ...lead, lifecycle_stage: firstColumnId }
            : lead
        )
      );

      setColumns(prevColumns => prevColumns.filter(col => col.id !== columnId));
    }
  }, [columns]);

  const handleCardClick = useCallback((leadId: string) => {
    navigate(`/customer-360?highlight=${leadId}`);
  }, [navigate]);

  const handleCancelEdit = useCallback(() => {
    setEditingColumn(null);
    setNewColumnTitle('');
  }, []);

  if (loading) {
    return (
      <div className="planner-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="planner-page">
      <div className="planner-header">
        <h1>Planner de Leads</h1>
        <div className="planner-actions">
          <button className="btn-add-column" onClick={handleAddColumn}>
            + Adicionar Coluna
          </button>
          <button className="btn-refresh" onClick={loadLeads}>
            🔄 Atualizar
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="planner-board">
          {columns.map(column => (
            <ColumnComponent
              key={column.id}
              column={column}
              leads={leadsByColumn[column.id] || []}
              isEditing={editingColumn === column.id}
              editTitle={newColumnTitle}
              onEditTitleChange={setNewColumnTitle}
              onSave={handleSaveColumn}
              onCancel={handleCancelEdit}
              onEdit={() => handleEditColumn(column.id)}
              onDelete={() => handleDeleteColumn(column.id)}
              onCardClick={handleCardClick}
              isDragging={isDragging}
            />
          ))}
        </div>

        <DragOverlay>
          {activeId ? (
            <LeadCardComponent
              lead={leads.find(l => l.id === activeId)!}
              isDragging
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

// Componente Row para virtualização
interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    leads: Customer360Detail[];
    onCardClick: (leadId: string) => void;
    isDragging: boolean;
  };
}

const Row = memo(function Row({ index, style, data }: RowProps) {
  const lead = data.leads[index];
  if (!lead) return null;

  return (
    <div style={{ ...style, paddingBottom: '12px' }}>
      <LeadCardComponent lead={lead} onCardClick={data.onCardClick} />
    </div>
  );
});

interface ColumnComponentProps {
  column: Column;
  leads: Customer360Detail[];
  isEditing: boolean;
  editTitle: string;
  onEditTitleChange: (title: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCardClick: (leadId: string) => void;
  isDragging: boolean;
}

const ColumnComponent = memo(function ColumnComponent({
  column,
  leads,
  isEditing,
  editTitle,
  onEditTitleChange,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  onCardClick,
  isDragging: parentIsDragging,
}: ColumnComponentProps) {
  const { setNodeRef } = useSortable({ id: column.id });
  const listRef = useRef<List>(null);

  const leadIds = useMemo(() => leads.map(l => l.id), [leads]);

  // Altura estimada de cada card (incluindo margin-bottom)
  const CARD_HEIGHT = 162; // 150px card + 12px margin-bottom

  // Altura máxima da lista virtualizada
  const MAX_LIST_HEIGHT = Math.min(window.innerHeight - 250, 800);

  // Dados para passar para o Row
  const rowData = useMemo(() => ({
    leads,
    onCardClick,
    isDragging: parentIsDragging,
  }), [leads, onCardClick, parentIsDragging]);

  // Função para obter altura de cada item (pode variar)
  const getItemSize = useCallback((index: number) => {
    return CARD_HEIGHT;
  }, []);

  return (
    <div ref={setNodeRef} className="planner-column">
      <div className="column-header">
        {isEditing ? (
          <div className="column-edit-form">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => onEditTitleChange(e.target.value)}
              onBlur={onSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSave();
                } else if (e.key === 'Escape') {
                  onCancel();
                }
              }}
              autoFocus
              className="column-title-input"
            />
          </div>
        ) : (
          <>
            <h3 className="column-title">{column.title}</h3>
            <div className="column-actions">
              <button className="btn-edit-column" onClick={onEdit} title="Editar">
                ✏️
              </button>
              <button className="btn-delete-column" onClick={onDelete} title="Deletar">
                🗑️
              </button>
            </div>
          </>
        )}
        <span className="column-count">{leads.length}</span>
      </div>

      <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
        <div className="column-cards-virtualized">
          {leads.length > 0 ? (
            // Durante drag, renderizar todos os cards para drag-and-drop funcionar
            // Fora do drag, usar virtualização para performance
            parentIsDragging ? (
              <div className="column-cards">
                {leads.map(lead => (
                  <LeadCardComponent key={lead.id} lead={lead} onCardClick={onCardClick} />
                ))}
              </div>
            ) : (
              <List
                ref={listRef}
                height={MAX_LIST_HEIGHT}
                itemCount={leads.length}
                itemSize={getItemSize}
                itemData={rowData}
                width="100%"
                overscanCount={5}
              >
                {Row}
              </List>
            )
          ) : (
            <div className="column-empty">
              <p>Nenhum lead nesta coluna</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
});

interface LeadCardComponentProps {
  lead: Customer360Detail;
  isDragging?: boolean;
  onCardClick?: (leadId: string) => void;
}

const LeadCardComponent = memo(function LeadCardComponent({ lead, isDragging = false, onCardClick }: LeadCardComponentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: lead.id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  }), [transform, transition, isSortableDragging]);

  const formatCurrency = useCallback((value: string | number | null) => {
    if (value === null || value === undefined) return 'R$ 0';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'R$ 0';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  }, []);

  const handleClick = useCallback(() => {
    onCardClick?.(lead.id);
  }, [onCardClick, lead.id]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`lead-card ${isDragging ? 'dragging' : ''}`}
      onClick={handleClick}
    >
      <div className="card-header">
        <h4 className="card-name">{lead.full_name || lead.email || 'Sem nome'}</h4>
        {lead.tier && (
          <span className={`card-tier tier-${lead.tier}`}>{lead.tier}</span>
        )}
      </div>
      <div className="card-body">
        {lead.email && (
          <div className="card-info">
            <span className="card-label">📧</span>
            <span className="card-value">{lead.email}</span>
          </div>
        )}
        {lead.phone && (
          <div className="card-info">
            <span className="card-label">📞</span>
            <span className="card-value">{lead.phone}</span>
          </div>
        )}
        {lead.net_revenue && Number(lead.net_revenue) > 0 && (
          <div className="card-info">
            <span className="card-label">💰</span>
            <span className="card-value">{formatCurrency(lead.net_revenue)}</span>
          </div>
        )}
        {lead.city && lead.uf && (
          <div className="card-info">
            <span className="card-label">📍</span>
            <span className="card-value">{lead.city}, {lead.uf}</span>
          </div>
        )}
      </div>
      {lead.churn_score !== null && lead.churn_score !== undefined && (
        <div className="card-footer">
          <div className="card-score">
            <span>Churn: {Number(lead.churn_score).toFixed(1)}</span>
          </div>
        </div>
      )}
    </div>
  );
});

